import {
  REVIEW_REASON_LABELS,
  formatMinor,
  type CatchUpReport,
  type CatchUpRun,
  type MonthState,
} from '@hvac/contracts';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  PageHeader,
  Pill,
  Skeleton,
  StatBlock,
  type PillTone,
} from '@hvac/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import {
  askAboutMissingPaper,
  catchUpKeys,
  catchUpReportQuery,
  runCatchUp,
  signCatchUp,
} from '../../shared/api/catch-up-queries.js';
import { sessionQuery } from '../../shared/api/queries.js';

/**
 * Catch up the books.
 *
 * A pile of paper becomes a statement an accountant can work from: month by
 * month, ready, nearly ready (a signature away) or blocked, and by what, with
 * the dollars that are unexplained. The button finishes the reading and files
 * what the system is sure of; signing that batch is a second, deliberate
 * click. When the reading finishes, the run goes once more by itself, so
 * what was just read is filed and matched too.
 */

const STATE_LABEL: Readonly<Record<MonthState, string>> = {
  closed: 'Closed',
  ready: 'Ready to close',
  nearly: 'A signature away',
  blocked: 'Needs work',
  quiet: 'Nothing happened',
};
const STATE_TONE: Readonly<Record<MonthState, PillTone>> = {
  closed: 'neutral',
  ready: 'success',
  nearly: 'info',
  blocked: 'warning',
  quiet: 'neutral',
};

export function readingLeft(reading: CatchUpReport['reading']): number {
  return reading.toRead + reading.inFlight + reading.retrying + reading.dead;
}

export function runSummary(run: CatchUpRun): string {
  const parts: string[] = [];
  const sent = run.readingQueued + run.readingRevived;
  if (sent > 0) parts.push(`${String(sent)} document${sent === 1 ? '' : 's'} sent to be read`);
  if (run.duplicatesSetAside > 0)
    parts.push(`${String(run.duplicatesSetAside)} duplicates set aside`);
  if (run.filed > 0) parts.push(`${String(run.filed)} filed as drafts`);
  if (run.bankMatched > 0) parts.push(`${String(run.bankMatched)} bank lines matched`);
  if (run.returnsRebuilt > 0)
    parts.push(
      `${String(run.returnsRebuilt)} return${run.returnsRebuilt === 1 ? '' : 's'} brought up to date`,
    );
  const head =
    parts.length === 0
      ? 'Nothing new to do; the books are as caught up as they can get without a person.'
      : `Done: ${parts.join(', ')}.`;
  return run.moreToFile ? `${head} There is more to file; run it again to continue.` : head;
}

export function CatchUpPage() {
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';
  const permissions =
    session.organizations.find((entry) => entry.organization.id === organizationId)?.permissions ??
    [];
  const canWrite = permissions.includes('transaction:update');
  const queryClient = useQueryClient();
  const [lastRun, setLastRun] = useState<CatchUpRun | null>(null);
  const [error, setError] = useState<string | null>(null);

  const report = useQuery({
    ...catchUpReportQuery(organizationId),
    // While documents are being read the picture changes by the minute.
    refetchInterval: (query) =>
      query.state.data !== undefined && readingLeft(query.state.data.reading) > 0 ? 15_000 : false,
  });
  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: catchUpKeys.report(organizationId) });

  const run = useMutation({
    mutationFn: () => runCatchUp(organizationId),
    onSuccess: (result) => {
      setLastRun(result);
      setError(null);
      refresh();
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'The catch-up stopped.'),
  });

  // When the reading this run started has finished, go once more, so what
  // was just read is filed and matched as well.
  const awaitingReading = useRef(false);
  const left = report.data === undefined ? 0 : readingLeft(report.data.reading);
  useEffect(() => {
    if (lastRun !== null && left > 0) awaitingReading.current = true;
    if (awaitingReading.current && left === 0 && !run.isPending) {
      awaitingReading.current = false;
      run.mutate();
    }
  }, [left, lastRun, run]);

  const data = report.data;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Work"
        title="Catch up the books"
        description="Finishes the reading, files what is certain, matches the bank and brings the returns up to date. Then, month by month: how close the books are, and exactly what is left."
        action={
          <div className="flex gap-2 print:hidden">
            <Button
              variant="secondary"
              onClick={() => window.print()}
              disabled={data === undefined}
            >
              Print
            </Button>
            {canWrite ? (
              <Button onClick={() => run.mutate()} isLoading={run.isPending}>
                {lastRun === null ? 'Catch up now' : 'Run again'}
              </Button>
            ) : null}
          </div>
        }
      />

      {error === null ? null : <Alert tone="danger">{error}</Alert>}
      {lastRun === null ? null : (
        <Alert tone={lastRun.filingFailed > 0 ? 'warning' : 'success'}>
          {runSummary(lastRun)}
          {lastRun.filingFailed > 0
            ? ` ${String(lastRun.filingFailed)} could not be filed automatically and are waiting in Review.`
            : ''}
        </Alert>
      )}

      {data === undefined ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <>
          <Overview report={data} />
          <Reading report={data} />
          <Months report={data} />
          <Signable
            organizationId={organizationId}
            report={data}
            canWrite={canWrite}
            onDone={refresh}
          />
          <Decisions report={data} />
          <MissingPaper
            organizationId={organizationId}
            report={data}
            canWrite={canWrite}
            onDone={refresh}
          />
          <Returns report={data} />
        </>
      )}
    </div>
  );
}

function Overview({ report }: { report: CatchUpReport }) {
  const active = report.months.filter((month) => month.state !== 'quiet');
  const ready = active.filter(
    (month) => month.state === 'ready' || month.state === 'closed',
  ).length;
  const nearly = active.filter((month) => month.state === 'nearly').length;
  const decisions = report.review.reduce((sum, group) => sum + group.count, 0);
  return (
    <div className="grid gap-4 sm:grid-cols-4">
      <StatBlock
        label="Months ready"
        value={`${String(ready)} of ${String(active.length)}`}
        hint={nearly > 0 ? `${String(nearly)} more a signature away` : undefined}
      />
      <StatBlock
        label="Sure, waiting for a signature"
        value={String(report.signable.count)}
        hint={formatMinor(report.signable.grossMinor)}
      />
      <StatBlock label="Decisions for a person" value={String(decisions)} />
      <StatBlock
        label="Payments with no paper"
        value={formatMinor(report.missingPaper.totalMinor)}
        hint={`${String(report.missingPaper.count)} payments`}
        {...(report.missingPaper.count > 0 ? { tone: 'danger' as const } : {})}
      />
    </div>
  );
}

function Reading({ report }: { report: CatchUpReport }) {
  const reading = report.reading;
  const left = readingLeft(reading);
  if (left === 0 && reading.parked === 0) return null;
  const room = BigInt(reading.ceilingMinor) - BigInt(reading.spentMinor);
  const short = BigInt(reading.estimateMinor) > room;
  return (
    <Card>
      <CardHeader
        title={
          left > 0
            ? `${String(left)} document${left === 1 ? '' : 's'} still being read`
            : 'Reading is paused'
        }
        description="This page refreshes while they are read, then runs the catch-up once more by itself."
      />
      <CardBody className="flex flex-col gap-3 text-sm">
        {reading.parked > 0 ? (
          <Alert tone="warning">
            {reading.parked} {reading.parked === 1 ? 'is' : 'are'} waiting on this month&rsquo;s AI
            ceiling ({formatMinor(reading.spentMinor)} of {formatMinor(reading.ceilingMinor)} used).
            Nothing is lost; raise the ceiling to read them now.
          </Alert>
        ) : null}
        {short && left > 0 ? (
          <p className="text-content-muted">
            Reading the rest will cost about {formatMinor(reading.estimateMinor)}, more than the{' '}
            {formatMinor(room > 0n ? room.toString() : '0')} left under this month&rsquo;s ceiling.
            {reading.canChangeCeiling ? (
              <>
                {' '}
                <Link to="/settings" className="text-brand-600 hover:underline">
                  Raise the ceiling
                </Link>{' '}
                first, or the rest waits for next month.
              </>
            ) : (
              ' An owner can raise it in Settings.'
            )}
          </p>
        ) : (
          <p className="text-content-muted">
            About {formatMinor(reading.estimateMinor)} to read the rest, within this month&rsquo;s
            ceiling.
          </p>
        )}
      </CardBody>
    </Card>
  );
}

function Months({ report }: { report: CatchUpReport }) {
  const [open, setOpen] = useState<string | null>(null);
  const months = [...report.months].reverse();
  return (
    <Card>
      <CardHeader
        title="Month by month"
        description="Ready means nothing blocks closing it. A signature away means everything is drafted and sure. Open a month to see what is in the way."
      />
      <CardBody className="p-0">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-content-subtle">
            <tr>
              <th className="px-4 py-2 font-medium">Month</th>
              <th className="px-4 py-2 font-medium">State</th>
              <th className="px-4 py-2 text-right font-medium">Signed</th>
              <th className="px-4 py-2 text-right font-medium">Drafts</th>
              <th className="px-4 py-2 text-right font-medium">Unexplained</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {months.map((month) => (
              <MonthRow
                key={month.start}
                month={month}
                open={open === month.start}
                onToggle={() => setOpen(open === month.start ? null : month.start)}
              />
            ))}
          </tbody>
        </table>
      </CardBody>
    </Card>
  );
}

function MonthRow({
  month,
  open,
  onToggle,
}: {
  month: CatchUpReport['months'][number];
  open: boolean;
  onToggle: () => void;
}) {
  const blocking = month.checks.filter((check) => check.severity === 'blocking');
  return (
    <>
      <tr
        className={`cursor-pointer hover:bg-surface-sunken ${month.state === 'quiet' ? 'text-content-subtle' : ''}`}
        onClick={onToggle}
      >
        <td className="px-4 py-2 font-medium">{month.label}</td>
        <td className="px-4 py-2">
          <Pill tone={STATE_TONE[month.state]}>{STATE_LABEL[month.state]}</Pill>
        </td>
        <td className="tabular px-4 py-2 text-right">{month.confirmed}</td>
        <td className="tabular px-4 py-2 text-right">{month.drafts}</td>
        <td className="tabular px-4 py-2 text-right">
          {month.unexplainedMinor === '0' ? '' : formatMinor(month.unexplainedMinor)}
        </td>
      </tr>
      {open && month.checks.length > 0 ? (
        <tr className="bg-surface-sunken/50">
          <td colSpan={5} className="px-4 py-3">
            <ul className="flex flex-col gap-1 text-sm">
              {month.checks.map((check) => (
                <li key={check.kind} className="flex justify-between gap-4">
                  <span
                    className={
                      check.severity === 'blocking' ? 'text-content' : 'text-content-muted'
                    }
                  >
                    {check.headline}
                  </span>
                  <span className="tabular text-content-muted">{check.count}</span>
                </li>
              ))}
            </ul>
            {blocking.length > 0 ? (
              <Link
                to="/close"
                className="mt-2 inline-block text-sm text-brand-600 hover:underline"
              >
                Work through it on the Close page
              </Link>
            ) : null}
          </td>
        </tr>
      ) : null}
    </>
  );
}

function Signable({
  organizationId,
  report,
  canWrite,
  onDone,
}: {
  organizationId: string;
  report: CatchUpReport;
  canWrite: boolean;
  onDone: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const sign = useMutation({
    mutationFn: () => signCatchUp(organizationId),
    onSuccess: (outcome) => {
      setConfirming(false);
      setResult(
        `Signed ${String(outcome.signed)}.${outcome.skipped > 0 ? ` ${String(outcome.skipped)} turned out to need a look and stayed as drafts.` : ''}`,
      );
      onDone();
    },
  });
  if (report.signable.count === 0 && result === null) return null;
  return (
    <Card>
      <CardHeader
        title={`The system is sure of ${String(report.signable.count)} record${report.signable.count === 1 ? '' : 's'}`}
        description={`${formatMinor(report.signable.grossMinor)} of spending, ${formatMinor(report.signable.itcMinor)} of tax credits. Each has a known supplier, an account this business already uses for them, and nothing unclear. They are drafts until a person signs.`}
        actions={
          !canWrite || report.signable.count === 0 ? null : confirming ? (
            <div className="flex gap-2 print:hidden">
              <Button size="sm" onClick={() => sign.mutate()} isLoading={sign.isPending}>
                Sign {report.signable.count}, with my name
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <div className="flex gap-2 print:hidden">
              <Link to="/close" className="self-center text-sm text-brand-600 hover:underline">
                See the list
              </Link>
              <Button size="sm" onClick={() => setConfirming(true)}>
                Sign them
              </Button>
            </div>
          )
        }
      />
      {result === null ? null : (
        <CardBody>
          <Alert tone="success">{result}</Alert>
        </CardBody>
      )}
    </Card>
  );
}

/** A reason added to the database before the labels learn it still reads. */
const REASON_LABELS: Readonly<Record<string, string | undefined>> = REVIEW_REASON_LABELS;
const reasonLabel = (reason: string): string => REASON_LABELS[reason] ?? reason.replace(/_/g, ' ');

function Decisions({ report }: { report: CatchUpReport }) {
  if (report.review.length === 0) return null;
  return (
    <Card>
      <CardHeader
        title="Decisions only a person can make"
        description="Grouped by the question being asked, so each group is one kind of decision made many times."
        actions={
          <Link to="/review" className="text-sm text-brand-600 hover:underline print:hidden">
            Open Review
          </Link>
        }
      />
      <CardBody>
        <ul className="grid gap-2 sm:grid-cols-2">
          {report.review.map((group) => (
            <li
              key={group.reason}
              className="flex justify-between rounded-[var(--radius-control)] border border-border px-3 py-2 text-sm"
            >
              <span>{reasonLabel(group.reason)}</span>
              <span className="tabular font-medium">{group.count}</span>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}

function MissingPaper({
  organizationId,
  report,
  canWrite,
  onDone,
}: {
  organizationId: string;
  report: CatchUpReport;
  canWrite: boolean;
  onDone: () => void;
}) {
  const unasked = report.missingPaper.lines.filter((line) => !line.asked);
  const [chosen, setChosen] = useState<ReadonlySet<string> | null>(null);
  const selected = chosen ?? new Set(unasked.map((line) => line.bankTransactionId));
  const [message, setMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const ask = useMutation({
    mutationFn: () => askAboutMissingPaper(organizationId, [...selected].slice(0, 100)),
    onSuccess: (result) => {
      setMessage(result.message);
      setChosen(null);
      onDone();
    },
  });
  if (report.missingPaper.count === 0) return null;

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setChosen(next);
  };

  return (
    <Card>
      <CardHeader
        title={`${formatMinor(report.missingPaper.totalMinor)} left the account with no paper behind it`}
        description={`${String(report.missingPaper.count)} payments, largest first. Without a receipt the tax on them cannot be claimed. Ask the client in one message: each line gets a link they open on their phone, no sign-in.`}
        actions={
          canWrite && selected.size > 0 ? (
            <Button
              size="sm"
              onClick={() => ask.mutate()}
              isLoading={ask.isPending}
              className="print:hidden"
            >
              Ask the client about {Math.min(selected.size, 100)}
            </Button>
          ) : null
        }
      />
      <CardBody className="flex flex-col gap-3">
        {message === null || message === '' ? null : (
          <div className="flex flex-col gap-2 print:hidden">
            <textarea
              readOnly
              value={message}
              rows={Math.min(14, message.split('\n').length + 1)}
              className="w-full rounded-[var(--radius-control)] border border-border-strong bg-surface p-3 font-mono text-xs"
            />
            <div className="flex items-center gap-3">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  void navigator.clipboard.writeText(message).then(() => setCopied(true));
                }}
              >
                {copied ? 'Copied' : 'Copy the message'}
              </Button>
              <span className="text-xs text-content-muted">
                Paste it into a text or an email. Answers appear on the Questions page.
              </span>
            </div>
          </div>
        )}
        <ul className="divide-y divide-border rounded-[var(--radius-control)] border border-border text-sm">
          {report.missingPaper.lines.map((line) => (
            <li key={line.bankTransactionId} className="flex items-center gap-3 px-3 py-2">
              {canWrite && !line.asked ? (
                <input
                  type="checkbox"
                  className="print:hidden"
                  checked={selected.has(line.bankTransactionId)}
                  onChange={() => toggle(line.bankTransactionId)}
                  aria-label={`Ask about ${line.description}`}
                />
              ) : (
                <span className="w-[13px]" />
              )}
              <span className="tabular w-24 text-content-muted">{line.postedOn}</span>
              <span className="min-w-0 flex-1 truncate">{line.description}</span>
              {line.asked ? <Pill tone="info">Asked</Pill> : null}
              <span className="tabular w-24 text-right">{formatMinor(line.amountMinor)}</span>
            </li>
          ))}
        </ul>
        {report.missingPaper.count > report.missingPaper.lines.length ? (
          <p className="text-xs text-content-muted">
            The largest {report.missingPaper.lines.length} are shown; the rest are on the Bank page.
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}

function Returns({ report }: { report: CatchUpReport }) {
  if (report.returns.length === 0) {
    return (
      <Card>
        <CardHeader
          title="GST/HST returns"
          description="No filing periods are set up yet. Say how often this business files on the Tax page and the returns appear here, built from signed records."
        />
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader
        title="GST/HST returns"
        description="Each built from signed records only, so the figures move as records are signed."
        actions={
          <Link to="/tax" className="text-sm text-brand-600 hover:underline print:hidden">
            Open Tax
          </Link>
        }
      />
      <CardBody className="p-0">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-content-subtle">
            <tr>
              <th className="px-4 py-2 font-medium">Period</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 text-right font-medium">Collected</th>
              <th className="px-4 py-2 text-right font-medium">Credits</th>
              <th className="px-4 py-2 text-right font-medium">Net tax</th>
              <th className="px-4 py-2 font-medium">Due</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {report.returns.map((period) => (
              <tr key={period.periodId}>
                <td className="px-4 py-2">{period.label}</td>
                <td className="px-4 py-2 capitalize">{period.status.replace(/_/g, ' ')}</td>
                <td className="tabular px-4 py-2 text-right">
                  {formatMinor(period.collectedMinor)}
                </td>
                <td className="tabular px-4 py-2 text-right">{formatMinor(period.itcMinor)}</td>
                <td className="tabular px-4 py-2 text-right font-medium">
                  {formatMinor(period.netTaxMinor)}
                </td>
                <td className="px-4 py-2">{period.filingDueOn ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardBody>
    </Card>
  );
}
